// ============================================
// DASHBOARD PAGE
// ============================================
// TIER-4 UPDATE:
// [ADMİN-H2] Dashboard verileri düzeltildi
// [ADMİN-H3] Son aktiviteler alanı dolduruldu
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Building2,
  Briefcase,
  FileText,
  TrendingUp,
  AlertCircle,
  Clock,
  Anchor,
  ArrowRight,
  UserPlus,
  Shield,
  Star,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { PageHeader, StatCard } from '@/components';
import { adminApi } from '@/services/api';
import { cn, formatNumber, formatRelativeTime } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/')({
  component: DashboardPage,
});

// Activity icon mapping
const activityIcons: Record<string, { icon: any; color: string }> = {
  registration: { icon: UserPlus, color: 'text-blue-500 bg-blue-100' },
  application: { icon: FileText, color: 'text-green-500 bg-green-100' },
  verification: { icon: Shield, color: 'text-amber-500 bg-amber-100' },
  review: { icon: Star, color: 'text-purple-500 bg-purple-100' },
};

function DashboardPage() {
  const { t } = useTranslation('panel');

  // Dashboard verileri (istatistikler + grafikler)
  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const response = await adminApi.getDashboard();
      return (response as any)?.data || response;
    },
  });

  // Son aktiviteler
  const { data: rawActivities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['admin', 'recent-activities'],
    queryFn: async () => {
      try {
        const response = await adminApi.getRecentActivities(10);
        return (response as any)?.data || response || [];
      } catch {
        return [];
      }
    },
    retry: false,
  });

  const dashboardData = rawData;
  const activities = rawActivities || [];

  // Backend'den gelen grafik verileri
  const applicationTrend: any[] = dashboardData?.applicationTrend || [];
  const jobsByDepartment: any[] = dashboardData?.jobsByDepartment || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description')}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('dashboard.totalUsers')}
          value={dashboardData?.totalUsers || 0}
          icon={<Users className="h-6 w-6" />}
          iconColor="bg-blue-100 text-blue-600"
          change={{ 
            value: dashboardData?.newUsersThisMonth || 0, 
            label: t('dashboard.newThisMonth') 
          }}
          loading={isLoading && !isError}
        />
        <StatCard
          title={t('dashboard.activeJobs')}
          value={dashboardData?.activeJobs || 0}
          icon={<Briefcase className="h-6 w-6" />}
          iconColor="bg-green-100 text-green-600"
          change={{ 
            value: dashboardData?.newJobsThisMonth || 0, 
            label: t('dashboard.newThisMonth') 
          }}
          loading={isLoading && !isError}
        />
        <StatCard
          title={t('dashboard.pendingApplications')}
          value={dashboardData?.pendingApplications || 0}
          icon={<FileText className="h-6 w-6" />}
          iconColor="bg-amber-100 text-amber-600"
          change={{ 
            value: dashboardData?.applicationsThisMonth || 0, 
            label: t('dashboard.totalThisMonth') 
          }}
          loading={isLoading && !isError}
        />
        <StatCard
          title={t('dashboard.pendingVerifications')}
          value={dashboardData?.pendingVerifications || 0}
          icon={<AlertCircle className="h-6 w-6" />}
          iconColor="bg-red-100 text-red-600"
          loading={isLoading && !isError}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Application Trends */}
        <div className="lg:col-span-2 bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.applicationTrend')}
          </h3>
          <div className="h-72">
            {applicationTrend.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                Henüz yeterli veri yok
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={applicationTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.625rem',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="applications"
                  name={t('dashboard.applicationsLabel')}
                  stroke="#0086c9"
                  fill="#e0f2fe"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Jobs by Department */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.jobsByDepartment')}
          </h3>
          <div className="h-72">
            {jobsByDepartment.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                Aktif ilan yok
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={jobsByDepartment}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="department"
                  label={({ department, percent }) =>
                    `${department} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {jobsByDepartment.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.625rem',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* [ADMİN-H3] Recent Activity - Gerçek verilerle */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('dashboard.recentActivities')}
            </h3>
            {activities.length > 0 && (
              <span className="text-xs text-gray-500">
                {t('dashboard.lastActivities', { count: activities.length })}
              </span>
            )}
          </div>
          
          {activitiesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-4 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>{t('dashboard.noActivities')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity: any) => {
                const iconConfig = activityIcons[activity.type] || activityIcons.registration;
                const IconComponent = iconConfig.icon;
                
                return (
                  <div key={activity.id} className="flex items-start gap-4">
                    <div
                      className={cn(
                        'flex-shrink-0 p-2 rounded-full',
                        iconConfig.color
                      )}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {activity.description}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-xs text-gray-400">
                      {formatRelativeTime(activity.createdAt)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {activities.length > 0 && (
            <Link
              to="/users"
              className="mt-4 w-full py-2 text-sm font-medium text-maritime-600 hover:text-maritime-700 hover:bg-maritime-50 rounded-maritime transition-colors flex items-center justify-center gap-1"
            >
              {t('dashboard.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.quickStats')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Anchor className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.seafarers')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.totalSeafarers || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.companies')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.totalCompanies || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.totalJobs')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.totalJobs || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.monthlyApplications')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.applicationsThisMonth || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Star className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.totalReviews')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.totalReviews || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.newRegistrations')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(dashboardData?.newUsersThisMonth || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
